import { DriverAnalysis } from '../types'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine
} from 'recharts'

const ACTIVITY_COLORS = {
  driving: '#3b5998',
  work: '#b8860b',
  rest: '#e2e5ea',
  availability: '#5c5c8a',
}

export function OverviewTab({ analysis, infringementNotes = [] }: { analysis: DriverAnalysis; infringementNotes?: any[] }) {
  const { summary, daily_breakdown, driving_pattern } = analysis

  // Calculate open (unresolved) infringements by cross-referencing notes
  const resolvedStatuses = new Set(['resolved_no_action', 'resolved_action_taken', 'closed'])
  const resolvedKeys = new Set(
    infringementNotes
      .filter(n => resolvedStatuses.has(n.status || 'open'))
      .map(n => `${n.infringement_date}::${n.infringement_type}`)
  )
  const openInfringements = analysis.infringements.filter(
    inf => !resolvedKeys.has(`${inf.date}::${inf.type}`)
  )
  const totalInf = analysis.infringements.length
  const openCount = openInfringements.length
  const resolvedCount = totalInf - openCount

  // Daily driving chart
  const dailyChartData = daily_breakdown.map(d => ({
    date: d.date.slice(5), // MM-DD
    driving: +(d.driving_minutes / 60).toFixed(1),
    work: +(d.work_minutes / 60).toFixed(1),
    availability: +(d.availability_minutes / 60).toFixed(1),
    rest: +(d.rest_minutes / 60).toFixed(1),
    status: d.status,
  }))

  return (
    <div className="space-y-5">
      {/* Summary table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Period Summary</h3>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              <SummaryRow label="Total Driving" value={`${summary.total_driving_hours}h`} sub={`${summary.total_driving_minutes} min`} />
              <SummaryRow label="Total Other Work" value={`${summary.total_work_hours}h`} />
              <SummaryRow label="Total Rest" value={`${summary.total_rest_hours}h`} />
              <SummaryRow label="Total Availability" value={`${summary.total_availability_hours}h`} />
              <SummaryRow label="Days Worked" value={`${summary.days_worked}`} />
              <SummaryRow label="Avg Daily Driving" value={`${summary.avg_daily_driving_hours}h`} />
              <SummaryRow label="Total Distance" value={`${summary.total_distance_km.toLocaleString()} km`} sub={`avg ${summary.avg_daily_distance_km} km/day`} />
            </tbody>
          </table>
        </div>

        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Compliance Status</h3>
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              <SummaryRow
                label="Open Infringements"
                value={`${openCount}`}
                valueClass={openCount > 0 ? 'text-red-600 font-semibold' : 'text-green-700 font-semibold'}
              />
              <SummaryRow label="Resolved" value={`${resolvedCount}`} />
              <SummaryRow label="Total Detected" value={`${totalInf}`} />
              <SummaryRow label="Driving Blocks" value={`${driving_pattern.total_driving_blocks}`} />
              <SummaryRow label="Avg Block Length" value={`${driving_pattern.avg_driving_block_minutes} min`} />
              <SummaryRow label="Longest Block" value={`${driving_pattern.longest_block_minutes} min`} />
              <SummaryRow label="Vehicles Used" value={`${analysis.vehicles_used.length}`} />
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily Driving Bar Chart */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Daily Activity (Hours)</h3>
          <div className="flex items-center gap-3 text-[10px] text-gray-400">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: ACTIVITY_COLORS.driving }} /> Driving</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: ACTIVITY_COLORS.work }} /> Work</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: ACTIVITY_COLORS.availability }} /> Availability</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: ACTIVITY_COLORS.rest }} /> Rest</span>
            <span className="text-red-400">--- 9h limit</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={dailyChartData} barCategoryGap="15%">
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} />
            <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
            <Tooltip
              contentStyle={{ fontSize: 11, borderRadius: 4, border: '1px solid #e5e7eb' }}
              formatter={(value: number, name: string) => [`${value}h`, name.charAt(0).toUpperCase() + name.slice(1)]}
            />
            <ReferenceLine y={9} stroke="#ef4444" strokeDasharray="4 3" strokeWidth={1} />
            <Bar dataKey="rest" stackId="a" fill={ACTIVITY_COLORS.rest} />
            <Bar dataKey="availability" stackId="a" fill={ACTIVITY_COLORS.availability} />
            <Bar dataKey="work" stackId="a" fill={ACTIVITY_COLORS.work} />
            <Bar dataKey="driving" stackId="a" fill={ACTIVITY_COLORS.driving} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Weekly Compliance Summary */}
      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Weekly Driving Summary</h3>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-1.5 text-xs font-semibold text-gray-500">Week</th>
              <th className="text-right py-1.5 text-xs font-semibold text-gray-500">Driving</th>
              <th className="text-right py-1.5 text-xs font-semibold text-gray-500">Days</th>
              <th className="text-right py-1.5 text-xs font-semibold text-gray-500">Fortnight</th>
              <th className="text-center py-1.5 text-xs font-semibold text-gray-500">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {analysis.weekly_breakdown.map((w) => (
              <tr key={w.week}>
                <td className="py-1.5 text-gray-700 font-medium">{w.week}</td>
                <td className={`py-1.5 text-right ${w.driving_hours > 56 ? 'text-red-600 font-semibold' : w.driving_hours > 50 ? 'text-amber-600' : 'text-gray-700'}`}>{w.driving_hours}h</td>
                <td className="py-1.5 text-right text-gray-500">{w.days_driven}</td>
                <td className={`py-1.5 text-right ${w.fortnight_driving_hours > 90 ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>{w.fortnight_driving_hours}h</td>
                <td className="py-1.5 text-center">
                  <span className={`inline-block w-2 h-2 rounded-full ${
                    w.weekly_status === 'ok' ? 'bg-green-500' :
                    w.weekly_status === 'warning' ? 'bg-amber-500' : 'bg-red-500'
                  }`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function SummaryRow({ label, value, sub, valueClass }: { label: string; value: string; sub?: string; valueClass?: string }) {
  return (
    <tr>
      <td className="py-1.5 text-gray-500">{label}</td>
      <td className={`py-1.5 text-right font-medium ${valueClass || 'text-gray-900'}`}>
        {value}
        {sub && <span className="text-xs text-gray-400 ml-1.5">{sub}</span>}
      </td>
    </tr>
  )
}
